import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import {
  Card,
  ChevronDownLargeIcon,
  CircleButton,
  CloseButton,
  CountChip,
  IconButton,
  MapButton,
  MapLayersIcon,
  SLIDE_UP_ANIMATE,
  SLIDE_UP_INITIAL,
} from '@hcs/design-system';
import { useShowHideIntercomLauncher } from '@hcs/help-chat';
import { LayersControlConfig } from '@hcs/types';
import { FIXED_QUERY_SELECTOR } from '@hcs/webapps';

import { VECTILES_METRICS_GROUP_CONFIG } from '../../constants';
import { useHcMap } from '../../hooks';

import { VectilesMetricControl } from './VectilesMetricControl';
import { VectilesMetricGroupControl } from './VectilesMetricGroupControl';

import styles from './HcMapLayersControl.module.css';

interface Props extends LayersControlConfig {
  mapId: string;
  className?: string;
  controlStyle?: 'list' | 'buttons' | 'mobilePanel';
  triggerHide?: number;
}
const dataHcName = 'heatmaps-control';
export const HcMapLayersControl = ({
  mapId,
  vectilesMetricGroups,
  position,
  toggles,
  className,
  controlStyle = 'list',
  triggerHide,
}: Props) => {
  const {
    mapState,
    actions: { hcMapShowMarkers, hcMapChangeVectilesMetricGroup },
  } = useHcMap(mapId);
  const [flowStarted, setFlowStarted] = useState(
    !!mapState?.heatmap.vectilesMetricId ||
      !!mapState?.heatmap.vectilesMetricGroup,
  );
  const [isActiveLayerControlHidden, setIsActiveLayerControlHidden] =
    useState(false);
  const fixedEle = document.querySelector(FIXED_QUERY_SELECTOR);
  const { vectilesMetricId, vectilesMetricGroup } = mapState?.heatmap || {};
  const showLayerControl =
    (flowStarted || vectilesMetricId) && !isActiveLayerControlHidden;

  // Hide the Intercom launcher while the mobilePanel style control is open
  useShowHideIntercomLauncher({
    shouldHide: !!(showLayerControl && controlStyle === 'mobilePanel'),
    hideId: 'hc-map-layers-control',
  });

  useEffect(() => {
    // Reset flow when layer is turned off
    if (flowStarted && !vectilesMetricId && !vectilesMetricGroup) {
      setFlowStarted(false);
    } // Cannot add flowStarted to deps because it will cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vectilesMetricId, vectilesMetricGroup]);

  // Trigger hiding control from ancestor components
  useEffect(() => {
    if (triggerHide) {
      setIsActiveLayerControlHidden(true);
    }
  }, [triggerHide]);

  if (!mapState) return null;
  const handleFlowStart = () => {
    setFlowStarted(true);
    setIsActiveLayerControlHidden(false);
  };

  const onClose = () => {
    hcMapShowMarkers({ mapId, showMarkers: true });
    hcMapChangeVectilesMetricGroup({
      mapId,
      vectilesMetricGroup: null,
    });
    setFlowStarted(false);
    setIsActiveLayerControlHidden(true);
  };

  const onHide = () => {
    setIsActiveLayerControlHidden(true);
  };

  return (
    <>
      <div
        data-hc-name={dataHcName}
        className={classNames(
          styles.HcMapLayerControl,
          styles[position],
          className,
        )}
      >
        {controlStyle === 'buttons' ? (
          !vectilesMetricId && (
            <div className={styles.ButtonsContainer}>
              {vectilesMetricGroups.map((vectilesMetricGroup) => (
                <MapButton
                  dataHcName={`${dataHcName}-${vectilesMetricGroup}-button`}
                  className={styles.LayerGroupButton}
                  key={vectilesMetricGroup}
                  onClick={(event) => {
                    hcMapChangeVectilesMetricGroup({
                      mapId,
                      vectilesMetricGroup: vectilesMetricGroup,
                    });
                    handleFlowStart();
                  }}
                >
                  {VECTILES_METRICS_GROUP_CONFIG[vectilesMetricGroup].label}
                </MapButton>
              ))}
            </div>
          )
        ) : controlStyle === 'mobilePanel' ? (
          <CircleButton
            size={'lg'}
            buttonStyle="primary"
            dataHcName={`${dataHcName}-switch-to-map-button`}
            ariaLabel={'Map Layers'}
            onClick={handleFlowStart}
            className={styles.CircleButton}
          >
            <MapLayersIcon />
            {vectilesMetricId && (
              <CountChip className={styles.CountChip} count={1} />
            )}
          </CircleButton>
        ) : (
          !flowStarted &&
          !vectilesMetricGroup &&
          !vectilesMetricId && (
            <MapButton
              dataHcName={`${dataHcName}-start-button`}
              onClick={handleFlowStart}
              label="Map Layers"
            />
          )
        )}
        {showLayerControl && controlStyle !== 'mobilePanel' && (
          <Card
            dataHcName={`${dataHcName}-controls`}
            noPadding
            theme={{ Card: styles.Card }}
          >
            {!vectilesMetricGroup && !vectilesMetricId && (
              <VectilesMetricGroupControl
                mapId={mapId}
                vectilesMetricGroups={vectilesMetricGroups}
              />
            )}
            {vectilesMetricId && (
              <VectilesMetricControl
                className={styles.VectilesMetricControl}
                mapId={mapId}
                toggles={toggles}
                hideMonochrome
              />
            )}
            <CloseButton
              ariaLabel="Close map layer"
              size="sm"
              className={styles.CloseButton}
              dataHcName={`${dataHcName}-close-button`}
              onClick={onClose}
            />
          </Card>
        )}
      </div>
      {controlStyle === 'mobilePanel' &&
        showLayerControl &&
        fixedEle &&
        createPortal(
          <AnimatePresence>
            <motion.div
              key="panel"
              initial={SLIDE_UP_INITIAL}
              animate={SLIDE_UP_ANIMATE}
              className={styles.MobilePanelControl}
            >
              {!vectilesMetricGroup && !vectilesMetricId && (
                <>
                  <h2 className={styles.MobilePanelTitle}>Map Layers</h2>
                  <VectilesMetricGroupControl
                    mapId={mapId}
                    vectilesMetricGroups={vectilesMetricGroups}
                  />
                  <CloseButton
                    size="sm"
                    className={styles.CloseButton}
                    dataHcName={`${dataHcName}-close-button`}
                    onClick={onClose}
                  />
                </>
              )}
              {vectilesMetricId && (
                <>
                  <VectilesMetricControl
                    className={styles.VectilesMetricControl}
                    mapId={mapId}
                    toggles={toggles}
                    hideMonochrome
                  />
                  <IconButton
                    label="Hide"
                    className={styles.MobileHideButton}
                    ariaLabel={'Hide map layers control'}
                    dataHcName="hide-map-layers-control"
                    icon={<ChevronDownLargeIcon size="sm" />}
                    onClick={onHide}
                  />
                  <CloseButton
                    label="Close"
                    ariaLabel="Close map layer"
                    size="sm"
                    className={styles.CloseButton}
                    dataHcName={`${dataHcName}-close-button`}
                    onClick={onClose}
                  />
                </>
              )}
            </motion.div>
            {!vectilesMetricGroup && !vectilesMetricId && (
              <motion.div
                key="screen"
                initial={SLIDE_UP_INITIAL}
                animate={SLIDE_UP_ANIMATE}
                className={styles.MobilePanelScreen}
                onClick={onClose}
              />
            )}
          </AnimatePresence>,
          fixedEle,
        )}
    </>
  );
};
